import React, { useEffect, useRef, useState } from 'react';
import TransactionEditor from 'views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import i18n from 'i18n';
import _ from 'lodash';
import { Transaction, TransactionWrapper } from 'types/rest/assetCatalogTypes';
import { useDispatch, useSelector } from 'react-redux';
import { postTransaction, putTransaction } from 'store/transactions/transactionsActions';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { Col, Row } from 'react-bootstrap';
import DataDefinitionPanelAdapter from '../tdm/DataDefinitionPanelAdapter';
import {
  getAccountAiConsent,
  getActiveAccountId,
  getActiveWorkspaceId,
  getBlazemeterAppUrl,
  getUserAiConsent,
  isArdBetaFeatureEnabled,
  isPrivateCloud,
  isPrivateDataEnabled,
} from '../../utils/bzmUserConfigUtil';
import DataDefinitionButton from '../tdm/DataDefinitionButton/DataDefinitionButton';
import DataDefinitionPanelContextAdapter from '../tdm/DataDefinitionPanelContextAdapter';

interface EditTransactionModalProps {
  transaction: Transaction;
  isReadOnly: boolean;
  onClose: any;
  /** On successful update of trasnaction */
  onUpdate?: (transaction: Transaction) => void;
  isNew: boolean;
}
const EditTransactionModal = (props: EditTransactionModalProps) => {
  // In order to gain access to the child component instance,
  // you need to assign it to a `ref`, so we call `useRef()` to get one
  const childRef: any = useRef();
  const dispatch = useDispatch();
  const isBlazeDataFeatureEnabled = !isPrivateCloud();
  const [isDataPanelActive, setIsDataPanelActive] = useState(isBlazeDataFeatureEnabled);

  const dataModel = useSelector((state: any) => state.dataModel);
  const sharedFolders = useSelector((state: any) => state.sharedFolders);
  const services = useSelector((state: any) => state.services?.services);

  const triggerSaveAction = () => {
    if (!_.isNil(childRef) && !_.isNil(childRef.current)) {
      childRef.current.saveTransactionHandler();
    }
  };

  const saveTransactionHandler = async (transactionWrapper: TransactionWrapper) => {
    try {
      if (props.isNew) {
        await postTransaction(transactionWrapper);
        dispatch(
          showToastNotification(
            ToastType.Success,
            `${i18n.t('transactions:actions.successfullyCreatedTxn')} ${transactionWrapper.transaction.name}`,
          ),
        );
      } else {
        await putTransaction(transactionWrapper);
        dispatch(
          showToastNotification(
            ToastType.Success,
            `${i18n.t('transactions:actions.successfullyUpdatedTxn')} ${transactionWrapper.transaction.name}`,
          ),
        );
      }
      if (props.onUpdate) {
        await props.onUpdate(transactionWrapper.transaction);
      }
      await props.onClose();
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.error', error.toString());
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `Could not update transaction ${transactionWrapper.transaction.name}`,
          errorMsg,
        ),
      );
    }
  };

  return (
    <Modal
      enforceFocus={false}
      dialogClassName="modal-full-screen"
      show={true}
      centered={true}
      backdrop={true}
      onHide={props.onClose}
    >
      <Modal.Header>
        <h5 className="modal-title">Edit Transaction: {props.transaction.name}</h5>
        <button onClick={props.onClose} type="button" className="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>
      <Modal.Body className={'position-relative'}>
        {isBlazeDataFeatureEnabled && (
          <div className={'text-right p-1 mb-4'}>
            <DataDefinitionButton
              onClick={() => {
                setIsDataPanelActive(!isDataPanelActive);
              }}
              paramCount={dataModel.paramCount + _.size(dataModel.globalEntity)}
              isLoading={dataModel.isLoading || false}
            />
          </div>
        )}

        <Row style={{ height: '100%' }}>
          <Col>
            <div className="bg-light d-flex" style={{ height: '100%' }}>
              <div style={{ width: '100%' }}>
                <TransactionEditor
                  ref={childRef}
                  serviceId={props.transaction.serviceId}
                  onSave={saveTransactionHandler}
                  transaction={props.transaction}
                  isCreate={false}
                  isReadOnly={props.isReadOnly}
                  isEmbedded={true}
                />
              </div>
            </div>
          </Col>
          {isDataPanelActive && (
            <Col className={'col-auto'}>
              <DataDefinitionPanelContextAdapter
                workspaceId={getActiveWorkspaceId()}
                isPanelVisible={true}
                onClose={() => {
                  setIsDataPanelActive(false);
                }}
                selectedServiceId={props.transaction.serviceId as number}
                services={services}
                accountId={getActiveAccountId()}
                useExpressionEditor={isArdBetaFeatureEnabled()}
                dataModel={dataModel}
                isReadOnly={false}
                aiConsent={getUserAiConsent()}
                accountAiConsent={getAccountAiConsent()}
              >
                <DataDefinitionPanelAdapter
                  workspaceId={getActiveWorkspaceId()}
                  services={services}
                  selectedServiceId={props.transaction.serviceId as number}
                  onClose={() => {
                    setIsDataPanelActive(false);
                  }}
                  sharedFolders={sharedFolders}
                  isPrivateDataAccount={isPrivateDataEnabled()}
                  accountId={getActiveAccountId()}
                  blazeBaseUrl={getBlazemeterAppUrl()}
                  dataModel={dataModel}
                  onChangeService={() => _.noop()} // Service is not going to be changed
                  showIterations={false}
                />
              </DataDefinitionPanelContextAdapter>
            </Col>
          )}
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" onClick={props.onClose}>
          {i18n.t('common:actions.cancel')}
        </Button>

        <Button variant="primary" onClick={triggerSaveAction}>
          {i18n.t('common:actions.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditTransactionModal;
